import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { DepartmentBadge } from '@/entities/department'
import { type EmployeeOption, EmployeePicker } from '@/entities/employee'
import {
  CustomerVisibleBadge,
  type Invitee,
  type MeetingDetail,
  MeetingRefs,
  MeetingStateBadge,
  meetingDetailQueryOptions,
  type ParticipantKind,
  type ParticipantState,
} from '@/entities/meeting'
import { useRequireSession } from '@/entities/session'
import { AdmitGuestsPanel } from '@/features/admit-guests'
import { DeleteMeetingControl } from '@/features/delete-meeting'
import { EditMeetingDialog } from '@/features/edit-meeting'
import { EndMeetingControl } from '@/features/end-meeting'
import { useInviteEmployees, useUninviteEmployee } from '@/features/invite-employees'
import { CustomerVisibilityControl } from '@/features/set-customer-visibility'
import { VisibilityControl } from '@/features/set-visibility'
import { ApiError } from '@/shared/api'
import {
  copyText,
  formatDurationBetween,
  formatMeetingStart,
  formatTime,
  formatTimeNear,
} from '@/shared/lib'
import { Avatar } from '@/shared/ui/avatar'
import { Button } from '@/shared/ui/button'
import { Card } from '@/shared/ui/card'
import { Chip, type ChipTone } from '@/shared/ui/chip'
import { Dialog } from '@/shared/ui/dialog'
import { Icon } from '@/shared/ui/icon'
import { PageHeader } from '@/shared/ui/page-header'
import { Spinner } from '@/shared/ui/spinner'
import { toast } from '@/shared/ui/toast'
import { ZoneNote } from '@/shared/ui/zone-note'
import { RecordingSection, TranscriptSection } from '@/widgets/meeting-artifacts'

export const Route = createFileRoute('/meetings_/$id')({
  // Loader только ради тайтла (название встречи): греет тот же query-кэш, что
  // и компонент. Ошибку (401/404) не пробрасываем — компонент сам рисует статус.
  loader: async ({ context, params }) => {
    try {
      return await context.queryClient.ensureQueryData(meetingDetailQueryOptions(params.id))
    } catch {
      return null
    }
  },
  head: ({ loaderData }) => ({
    meta: [{ title: `${loaderData?.title ?? 'Встреча'} · STIT Телемост` }],
  }),
  component: MeetingDetailPage,
})

const kindLabel: Record<ParticipantKind, string> = {
  employee: 'Сотрудник',
  guest: 'Гость',
  customer: 'Заказчик',
}

const stateLabel: Record<ParticipantState, string> = {
  waiting: 'Ожидает',
  joined: 'В сети',
  left: 'Вышел',
}

const stateTone: Record<ParticipantState, ChipTone> = {
  waiting: 'warning',
  joined: 'success',
  left: 'neutral',
}

// Временна́я часть шапки по состоянию: scheduled — плановое начало, active —
// фактический старт, ended — интервал с длительностью. startedAt появился
// позже createdAt — у старых встреч честный фолбэк на время создания.
function meetingTimeInfo(meeting: MeetingDetail): string | undefined {
  const startedAt = meeting.startedAt ?? meeting.createdAt
  if (meeting.state === 'scheduled' && meeting.scheduledAt)
    return `начало ${formatMeetingStart(meeting.scheduledAt)}`
  if (meeting.state === 'active') return `идёт с ${formatMeetingStart(startedAt)}`
  if (meeting.state === 'ended' && meeting.endedAt) {
    const duration = formatDurationBetween(startedAt, meeting.endedAt)
    // У запланированных — рядом плановое время: видно, во сколько договаривались
    // и во сколько реально началось.
    const planned = meeting.scheduledAt
      ? ` · запланирована на ${formatTimeNear(meeting.scheduledAt, startedAt)}`
      : ''
    // Конец — только время: дату уже несёт начало (обе границы — в APP_TZ).
    return `${formatMeetingStart(startedAt)} — ${formatTime(meeting.endedAt)}${duration ? ` · ${duration}` : ''}${planned}`
  }
  return undefined
}

function MeetingDetailPage() {
  useRequireSession()
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const [editing, setEditing] = useState(false)
  // Вход в scheduled-встречу активирует её для всех — просим явное подтверждение,
  // чтобы запланированные встречи не начинались случайным тапом по «Войти».
  const [confirmStart, setConfirmStart] = useState(false)
  const { data: meeting, isLoading, isError, error } = useQuery(meetingDetailQueryOptions(id))

  if (isLoading) {
    return (
      <div className="container-x px-6 py-10">
        <Spinner label="Загрузка встречи" />
      </div>
    )
  }

  if (isError || !meeting) {
    const notFound = error instanceof ApiError && error.status === 404
    return (
      <div className="container-x px-6 py-10">
        <p className="text-accent">
          {notFound
            ? 'Встреча не найдена. Проверьте ссылку: возможно, встреча была удалена.'
            : 'Не удалось загрузить встречу. Проверьте соединение и повторите.'}
        </p>
      </div>
    )
  }

  const joinable = meeting.state === 'active' || meeting.state === 'scheduled'
  const timeInfo = meetingTimeInfo(meeting)

  // Бэкенд отдаёт голый guest-токен; полную ссылку собираем от текущего origin.
  const guestUrl = meeting.guestLink
    ? meeting.guestLink.startsWith('http')
      ? meeting.guestLink
      : `${window.location.origin}/guest/${meeting.guestLink}`
    : undefined

  async function copyGuestLink(link: string) {
    const ok = await copyText(link)
    if (ok) toast.success('Ссылка для гостя скопирована')
    else toast.error('Не удалось скопировать ссылку')
  }

  return (
    <div className="container-x flex flex-col gap-6 px-4 py-8 sm:gap-8 sm:px-6 sm:py-10">
      <PageHeader
        title={meeting.title}
        // Момент здесь одиночный — зона стоит коротким тегом сразу за временем:
        // на бейдж в шапке рассчитывать нельзя, на мобиле его не видно.
        description={
          <>
            {`Организатор: ${meeting.organizer.name}`}
            {timeInfo && (
              <>
                {` · ${timeInfo} `}
                <ZoneNote />
              </>
            )}
          </>
        }
        // Статус — над заголовком (свойство встречи), в actions — только действия.
        status={
          <span className="flex flex-wrap items-center gap-2">
            <MeetingStateBadge state={meeting.state} />
            {meeting.customerVisible && <CustomerVisibleBadge />}
          </span>
        }
        actions={
          <>
            {joinable && (
              <Button
                size="sm"
                onClick={() => {
                  if (meeting.state === 'scheduled') setConfirmStart(true)
                  else void navigate({ to: '/join/$id', params: { id } })
                }}
              >
                Войти
              </Button>
            )}
            {/* Редактирование доступно любому сотруднику (PATCH на бэке — тоже);
                завершение/отмена — только организатору. */}
            <Button
              size="icon"
              variant="secondary"
              aria-label="Редактировать встречу"
              title="Редактировать встречу"
              onClick={() => setEditing(true)}
            >
              <Icon name="pencil" size={16} />
            </Button>
            {meeting.isOrganizer && meeting.state === 'active' && (
              <EndMeetingControl meetingId={id} mode="end" />
            )}
            {meeting.isOrganizer && meeting.state === 'scheduled' && (
              <EndMeetingControl meetingId={id} mode="cancel" />
            )}
            {meeting.canDelete && (
              <DeleteMeetingControl
                meetingId={id}
                onDeleted={() => void navigate({ to: '/meetings' })}
              />
            )}
          </>
        }
      />

      {/* Где идёт разговор: клик уводит в список с тем же срезом — как теги. */}
      <MeetingRefs
        meeting={meeting}
        className="-mt-2"
        onCompany={(company) => void navigate({ to: '/meetings', search: { company } })}
        onProject={(project) =>
          void navigate({
            to: '/meetings',
            // Проект без компании фильтр не показывает — отдаём пару.
            search: { ...(meeting.companyId ? { company: meeting.companyId } : {}), project },
          })
        }
      />

      {meeting.tags.length > 0 && (
        <div className="-mt-2 flex flex-wrap gap-1.5">
          {meeting.tags.map((t) => (
            <button
              key={t}
              type="button"
              aria-label={`Показать встречи с тегом ${t}`}
              onClick={() => void navigate({ to: '/meetings', search: { tag: t } })}
            >
              <Chip
                variant="soft"
                className="transition-colors hover:bg-bg-elevated hover:text-text-primary"
              >
                {t}
              </Chip>
            </button>
          ))}
        </div>
      )}

      {meeting.description && (
        <div className="-mt-2 flex flex-col gap-1">
          <p className="label">Описание встречи</p>
          <p className="whitespace-pre-line text-text-secondary">{meeting.description}</p>
        </div>
      )}

      {editing && <EditMeetingDialog meeting={meeting} onClose={() => setEditing(false)} />}

      <Dialog open={confirmStart} onClose={() => setConfirmStart(false)} title="Начать встречу?">
        <p className="text-sm text-text-secondary">
          {meeting.scheduledAt ? (
            <>
              {`Встреча запланирована на ${formatMeetingStart(meeting.scheduledAt)} `}
              <ZoneNote />
              {'. '}
            </>
          ) : (
            'Встреча ещё не началась. '
          )}
          Ваш вход начнёт её для всех участников.
        </p>
        <div className="mt-4 flex justify-end gap-3">
          <Button variant="secondary" size="sm" onClick={() => setConfirmStart(false)}>
            Не сейчас
          </Button>
          <Button size="sm" onClick={() => void navigate({ to: '/join/$id', params: { id } })}>
            Начать встречу
          </Button>
        </div>
      </Dialog>

      {/* Гостевая ссылка живёт только пока встреча живая — у завершённой/
          отменённой приглашать некуда. */}
      {meeting.guestLink && (meeting.state === 'active' || meeting.state === 'scheduled') && (
        <Card>
          {/* На мобиле — столбиком: ссылке достаётся вся ширина, кнопка ниже. */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div className="min-w-0">
              <p className="label">Ссылка для гостя</p>
              <p className="mt-1 truncate font-mono text-sm text-text-secondary">{guestUrl}</p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="self-start sm:self-auto"
              onClick={() => void copyGuestLink(guestUrl ?? '')}
            >
              <Icon name="copy" size={16} />
              Копировать
            </Button>
          </div>
        </Card>
      )}

      {/* Лобби — любому сотруднику: пригласить гостя может каждый, впустить тоже. */}
      {meeting.state === 'active' && (
        <Card>
          <h2 className="t-h3 mb-3">Ожидают входа</h2>
          <AdmitGuestsPanel meetingId={id} />
        </Card>
      )}

      {meeting.canManageAccess && (
        <div className="flex flex-col gap-3">
          <VisibilityControl meetingId={id} isPrivate={meeting.private} />
          {/* Рядом с приватностью: обе строки отвечают на «кто это увидит»,
              только одна — про своих, вторая — про клиента компании. */}
          <CustomerVisibilityControl meeting={meeting} />
        </div>
      )}

      <InviteesSection
        meetingId={id}
        invitees={meeting.invitees}
        state={meeting.state}
        canManage={meeting.canManageAccess}
      />

      <section className="flex flex-col gap-4">
        <h2 className="t-h3">Участники · {meeting.participants.length}</h2>
        {meeting.participants.length === 0 ? (
          <p className="text-text-tertiary">Пока никто не присоединился.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {meeting.participants.map((p) => (
              <li
                key={p.participantId}
                className="flex items-center gap-3 rounded-control bg-bg-surface px-4 py-3"
              >
                <Avatar name={p.name} />
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-2">
                    <p className="truncate text-text-primary">{p.name}</p>
                    <DepartmentBadge departments={p.departments} />
                  </div>
                  <p className="label">
                    {kindLabel[p.kind]}
                    {p.username ? ` · @${p.username}` : ''}
                  </p>
                </div>
                <Chip tone={stateTone[p.state]}>{stateLabel[p.state]}</Chip>
              </li>
            ))}
          </ul>
        )}
      </section>

      {meeting.state === 'ended' && meeting.recording.exists && (
        <section className="flex flex-col gap-4">
          <h2 className="t-h3">Запись</h2>
          <RecordingSection
            meetingId={id}
            title={meeting.title}
            dateIso={meeting.scheduledAt ?? meeting.createdAt}
          />
        </section>
      )}

      {meeting.state === 'ended' && meeting.transcriptStatus !== 'none' && (
        <section className="flex flex-col gap-4">
          <h2 className="t-h3">Транскрипт</h2>
          <TranscriptSection
            meetingId={id}
            status={meeting.transcriptStatus}
            progress={meeting.transcriptProgress}
            title={meeting.title}
            dateIso={meeting.scheduledAt ?? meeting.createdAt}
          />
        </section>
      )}
    </div>
  )
}

// Состав, заданный заранее: кого позвали и кто из них уже заходил. Звать вправе
// любой из круга встречи, поэтому пикер показан всем — отказ вернёт бэк.
// Круг встречи, заданный руками. До звонка это приглашение — «приходи»; у
// прошедшей звать уже некуда, там это доступ к записи и расшифровке. Действие
// на бэке одно и то же, разводим только формулировки: «позвать» на завершённую
// встречу читается абсурдно.
function InviteesSection({
  meetingId,
  invitees,
  state,
  canManage,
}: {
  meetingId: string
  invitees: Invitee[]
  state: MeetingDetail['state']
  canManage: boolean
}) {
  const [picked, setPicked] = useState<EmployeeOption[]>([])
  const past = state === 'ended'
  const invite = useInviteEmployees(meetingId, past)
  const uninvite = useUninviteEmployee(meetingId, past)
  const copy = past
    ? {
        title: 'Доступ к встрече',
        empty: 'Доступ никому не открывали.',
        action: 'Дать доступ',
        remove: 'Забрать',
        pending: 'только просмотр',
        hint: 'Кому открыть запись и расшифровку',
      }
    : {
        title: 'Приглашены',
        empty: 'Никого не звали заранее.',
        action: 'Позвать',
        remove: 'Убрать',
        pending: 'ждём',
        hint: 'Кого позвать на встречу',
      }

  // Отменённую встречу показывать нечего и звать на неё некого — бэк отвечает 410.
  if (state === 'cancelled') return null

  function send() {
    if (picked.length === 0) return
    invite.mutate(
      picked.map((p) => p.subject),
      { onSuccess: () => setPicked([]) },
    )
  }

  return (
    <section className="flex flex-col gap-4">
      <h2 className="t-h3">
        {copy.title} · {invitees.length}
      </h2>
      {invitees.length === 0 ? (
        <p className="text-text-tertiary">{copy.empty}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {invitees.map((i) => (
            <li
              key={i.subject}
              className="flex items-center gap-3 rounded-control bg-bg-surface px-4 py-3"
            >
              <Avatar name={i.name ?? i.subject} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-text-primary">{i.name ?? i.subject}</p>
                <p className="label">{i.username ? `@${i.username}` : 'сотрудник'}</p>
              </div>
              <Chip tone={i.joined ? 'success' : 'neutral'}>
                {i.joined ? 'был в звонке' : copy.pending}
              </Chip>
              {canManage && (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={uninvite.isPending}
                  onClick={() => uninvite.mutate(i.subject)}
                >
                  {copy.remove}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {/* Круг виден всем, кто видит встречу, но менять его вправе только свои —
          иначе отказ прилетал бы кликом. */}
      {canManage && (
        <div className="flex flex-col gap-3">
          <p className="label">{copy.hint}</p>
          <EmployeePicker value={picked} onChange={setPicked} disabled={invite.isPending} />
          <div>
            <Button size="sm" disabled={picked.length === 0 || invite.isPending} onClick={send}>
              {copy.action}
            </Button>
          </div>
        </div>
      )}
    </section>
  )
}
